import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Chip, Paper, Stack, Table, TableBody,
    TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import { financeApi, fmtKRWFull } from "../../api/financeApi";
import { ds } from "../../../../styles/designSystem";
import { formatDate } from "../../../../util/dateUtils";
import LoadingDots from "../../../../component/common/LoadingDots";
import FormModal from "../../../../component/common/FormModal";
import FormField from "../../../../component/common/FormField";
import { searchMatch } from "../../../../component/common/SearchField";
import TableToolbar from "../../../../component/common/TableToolbar";
import ComboBox from "../../../../component/common/ComboBox";
import ResizableTable from "../../../../component/common/ResizableTable";
import { useTableSort } from "../../../../util/useTableSort";
import { useFinanceLinks, VendorCell, ProjectCell } from "../../components/FinanceLinkProvider";
import { CASH_PLAN_STATUS, STATUS_ORDER, DEFAULT_STATUSES } from "../../constants/cashPlanStatus";

// 상태 메타·순서·기본값은 constants/cashPlanStatus.js 단일 원천에서 가져온다(중복 정의 제거).
const STATUS_META = CASH_PLAN_STATUS;

// 출처 — 결재연동이면 '결재', 그 외(수기·고정비 생성 등)는 '수기' 두 가지만.
const sourceKind = (r) => r.source === "APPROVAL"
    ? { label: "결재", color: "#4f46e5", bg: "#eef2ff" }
    : { label: "수기", color: "#64748b", bg: "#f1f5f9" };

// 결제 유형 — 항목에 지정된 값 우선. 없으면(과거 데이터) 고정비 생성분·카테고리로 추정.
const payKind = (r) => {
    const t = r.paymentType
        || ((r.source === "RECURRING" || (r.category && r.category.includes("고정"))) ? "고정비" : "일반");
    return t === "고정비"
        ? { label: "고정비", color: "#0f766e", bg: "#f0fdfa" }
        : { label: t,        color: "#475569", bg: "#f1f5f9" };
};

// 현재 페이지 결과 정렬용 — 금액만 커스텀(숫자), 나머지는 필드명 그대로
const SORT_ACC = { amount: r => r.amount || 0 };

/** 상세 모달의 읽기전용 값 박스 — '자금계획 수정' 폼의 입력칸과 같은 룩(테두리·둥근모서리·흰 배경). */
function ReadBox({ children, multiline = false }) {
    return (
        <Box sx={{
            border: "1px solid #e2e8f0", borderRadius: 1, bgcolor: "#fff",
            px: 1.5, py: multiline ? 1 : 0,
            minHeight: multiline ? 64 : 40,
            display: "flex", alignItems: multiline ? "flex-start" : "center",
            fontSize: 13, color: "#0f172a",
            whiteSpace: multiline ? "pre-wrap" : "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
            {(children === null || children === undefined || children === "") ? "—" : children}
        </Box>
    );
}

/**
 * 지출 예정 보고서 — 대표님이 "앞으로 나갈 돈"을 한 표로 검토하는 화면.
 * 시스템의 모든 지출 자금계획 중 예정·우선지급을 기본 표시하고, 상태 칩으로 완료 등도 필터해서 볼 수 있다.
 * 결재연동·고정비·수기 등 출처를 함께 보여줘 어디서 온 지출인지 바로 안다. (조회 전용)
 *
 * @param {string|null} lockStatus  지정 시 그 상태(예: "PRIORITY")로 고정 필터하고 상태 칩을 숨긴다.
 *                                  → "긴급 지출 보고서"(긴급=우선지급)를 이 컴포넌트 재사용으로 구현.
 * @param {string|null} noticeText  lockStatus 모드에서 상단에 띄울 안내 문구.
 */
export default function ExpensePlanReportTab({ departments = [], lockStatus = null, noticeText = null }) {
    const [raw, setRaw]         = useState(null);
    const [loading, setLoading] = useState(true);
    // lockStatus 가 있으면 그 상태 하나로 고정(긴급 보고서). 없으면 기본(예정·우선지급).
    const [statusOn, setStatusOn] = useState(() => new Set(lockStatus ? [lockStatus] : DEFAULT_STATUSES));
    const [deptId, setDeptId]   = useState("");
    const [q, setQ]             = useState("");   // 통합 검색어
    const [detailRow, setDetailRow] = useState(null);   // 수기·고정비 등 상세 모달 대상
    const { toggleSort, sortLabel, sortRows } = useTableSort("scheduledDate", "asc");
    const { openApproval } = useFinanceLinks();

    // 행 클릭 — 결재연동이면 결재 상세로, 아니면(수기·고정비) 상세 모달로
    const onRowClick = (r) => {
        if (r.source === "APPROVAL" && r.sourceId) openApproval(r.sourceId);
        else setDetailRow(r);
    };

    // 시스템의 모든 지출 계획을 한 번에 (넓은 구간 — 미완료는 예정일이 오늘 이전이어도 항상 포함)
    useEffect(() => {
        const from = formatDate(new Date(Date.now() - 365 * 86400000));
        const to   = formatDate(new Date(Date.now() + 3650 * 86400000));
        financeApi.getCashFlow({ from, to, includeCompleted: true, includeHold: true, page: 0, size: 100000 })
            .then(setRaw).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const toggleStatus = (s) => setStatusOn(prev => {
        const next = new Set(prev);
        if (next.has(s)) next.delete(s); else next.add(s);
        return next;
    });

    /**
     * 실제 적용 상태 필터.
     *
     * <p>[버그] lockStatus 를 useState 초기값으로만 쓰면, 부모(FinancePage)가 '지출 예정 보고서'와
     * '긴급 지출 보고서' 두 탭에 <b>같은 컴포넌트를 같은 트리 위치</b>로 렌더하기 때문에 탭을 전환해도
     * React 가 인스턴스를 재사용해 <b>초기화가 다시 돌지 않는다</b>. 그 결과 긴급 탭인데 이전 탭의
     * 필터(예정·우선지급)가 그대로 남아 '예정' 건들이 섞여 보였다.
     * [해결] lockStatus 가 있으면 state 와 무관하게 <b>렌더 시점에 그 상태로 강제</b>한다(부모가 key 로
     * 리마운트도 시키지만, 이 파생값이 있으면 리마운트 여부와 상관없이 항상 안전).
     */
    const effectiveStatuses = useMemo(
        () => (lockStatus ? new Set([lockStatus]) : statusOn),
        [lockStatus, statusOn]
    );

    const rows = useMemo(() => {
        const all = (raw?.rows || []).filter(r =>
            r.flowType === "EXPENSE"
            && effectiveStatuses.has(r.status)
            && (!deptId || String(r.deptId) === String(deptId))
            && searchMatch(q, [r.vendorName, r.title, r.deptName, r.authorName, r.category, r.channelName])
        );
        return sortRows(all, SORT_ACC);
    }, [raw, effectiveStatuses, deptId, q, sortRows]);

    // 요약 — 대표님용 3가지: 지급 예정 합계 · (긴급|우선지급) · 지연(연체)
    // 가운데 카드는 lockStatus 모드면 그 상태(긴급 보고서 → 긴급)를, 아니면 우선지급을 강조한다.
    const total         = rows.reduce((s, r) => s + (r.amount || 0), 0);
    const midStatus     = lockStatus || "PRIORITY";
    const midMeta       = STATUS_META[midStatus] || STATUS_META.PRIORITY;
    const priorityRows  = rows.filter(r => r.status === midStatus);
    const priorityTotal = priorityRows.reduce((s, r) => s + (r.amount || 0), 0);
    const overdueRows   = rows.filter(r => r.overdue && r.status !== "COMPLETED");
    const overdueTotal  = overdueRows.reduce((s, r) => s + (r.amount || 0), 0);

    return (
        <Box>
            {/* 긴급 보고서 등 lockStatus 모드 안내 배너 */}
            {lockStatus && noticeText && (
                <Box sx={{
                    display: "flex", alignItems: "center", gap: 1, mb: 2,
                    px: 2, py: 1.2, borderRadius: 1,
                    bgcolor: "#fffbeb", border: "1px solid #fde68a", color: "#92400e",
                    fontSize: 12.5, fontWeight: 600,
                }}>
                    <WarningAmberRoundedIcon sx={{ fontSize: 18 }} />
                    {noticeText}
                </Box>
            )}

            {/* 요약 카드 */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <Paper sx={{ ...ds.cardPadded, flex: 1, minWidth: 190 }}>
                    <Typography sx={ds.typography.label}>지급 예정 합계 (표시 기준)</Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#dc2626", mt: 0.5 }}>
                        {fmtKRWFull(total)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{rows.length}건</Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1, minWidth: 190 }}>
                    <Typography sx={ds.typography.label}>{midMeta.label}</Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: midStatus === "URGENT" ? "#dc2626" : "#d97706", mt: 0.5 }}>
                        {fmtKRWFull(priorityTotal)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{priorityRows.length}건 · 먼저 막을 지출</Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1, minWidth: 190 }}>
                    <Typography sx={ds.typography.label}>지연 (예정일 지남)</Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: overdueTotal > 0 ? "#dc2626" : "#0f172a", mt: 0.5 }}>
                        {fmtKRWFull(overdueTotal)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{overdueRows.length}건</Typography>
                </Paper>
            </Box>

            {/* 상태 필터 칩 + 부서.
                lockStatus 모드(긴급 지출 보고서)는 단일 상태 전용 화면이므로 상태 칩을 고를 이유가 없고,
                팀 필터도 쓰지 않는다 → 잠긴 상태 배지 하나만 보여준다. */}
            <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
                {lockStatus ? (
                    <>
                        {/*<Chip*/}
                        {/*    label={(STATUS_META[lockStatus] || {}).label || lockStatus}*/}
                        {/*    size="small"*/}
                        {/*    sx={{*/}
                        {/*        fontWeight: 800, fontSize: 11.5, height: 24,*/}
                        {/*        bgcolor: (STATUS_META[lockStatus] || {}).bg,*/}
                        {/*        color: (STATUS_META[lockStatus] || {}).color,*/}
                        {/*    }}*/}
                        {/*/>*/}
                    </>

                ) : STATUS_ORDER.map(s => {
                    const m = STATUS_META[s];
                    const on = statusOn.has(s);
                    return (
                        <Chip key={s} label={m.label} size="small" onClick={() => toggleStatus(s)}
                            sx={{
                                cursor: "pointer", fontWeight: 700, fontSize: 11.5, height: 24,
                                bgcolor: on ? m.bg : "#f8fafc",
                                color: on ? m.color : "#cbd5e1",
                                border: `1px solid ${on ? `${m.color}44` : "#e2e8f0"}`,
                            }} />
                    );
                })}
                {!lockStatus && (
                    <Box sx={{ width: 150 }}>
                        <ComboBox placeholder="전체 팀" clearable={false}
                            options={[{ value: "", label: "전체 팀" }, ...departments.map(d => ({ value: d.deptId, label: d.deptName }))]}
                            value={deptId}
                            onChange={setDeptId} />
                    </Box>
                )}

                {/* 검색 — 필터와 같은 줄 우측 끝. 이 표는 페이지 크기 변경을 지원하지 않으므로
                    'N개씩 보기' 는 넣지 않는다(눌러도 반영 못 하는 컨트롤은 혼란만 준다). */}
                <TableToolbar
                    onSearch={setQ}
                    placeholder="거래처·내용·부서·작성자 검색"
                    sx={{ flex: 1, mb: 0, minWidth: 240 }}
                />
            </Box>

            {/* 표 */}
            <Paper sx={ds.card}>
                {loading && <LoadingDots />}
                {!loading && <ResizableTable size="small" sx={ds.tableSx}>
                    <TableHead>
                        <TableRow>
                            {/* 번호 — 몇 번째 건인지 말할 수 있게. 정렬을 바꿔도 '지금 화면의 순서' 를 뜻한다. */}
                            <TableCell align="center" sx={{ width: 56 }}>번호</TableCell>
                            <TableCell align="center" sx={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => toggleSort("scheduledDate")}>{sortLabel("scheduledDate", "송금예정일")}</TableCell>
                            <TableCell align="center" sx={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => toggleSort("vendorName")}>{sortLabel("vendorName", "거래처")}</TableCell>
                            <TableCell align="center" sx={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => toggleSort("deptName")}>{sortLabel("deptName", "부서")}</TableCell>
                            <TableCell>내용</TableCell>
                            <TableCell align="center">결제 유형</TableCell>
                            <TableCell align="center">출처</TableCell>
                            <TableCell align="center" sx={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => toggleSort("authorName")}>{sortLabel("authorName", "작성자/기안자")}</TableCell>
                            <TableCell align="right" sx={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => toggleSort("amount")}>{sortLabel("amount", "금액")}</TableCell>
                            <TableCell align="center" sx={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>{sortLabel("status", "상태")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                                    해당 상태의 지출 예정이 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : <>
                            {rows.map((r, idx) => {
                                const sm  = STATUS_META[r.status] || STATUS_META.SCHEDULED;
                                const src = sourceKind(r);
                                const pay = payKind(r);
                                const isOverdue = r.overdue && r.status !== "COMPLETED";
                                return (
                                    <TableRow key={r.rowKey ?? r.id} hover
                                        onClick={() => onRowClick(r)}
                                        sx={{ cursor: "pointer" }}>
                                        <TableCell align="center" sx={{ fontSize: 12, color: "#94a3b8" }}>
                                            {idx + 1}
                                        </TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                                            {r.scheduledDate}
                                        </TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: 12 }} onClick={e => e.stopPropagation()}>
                                            <VendorCell vendorId={r.vendorId} vendorName={r.vendorName} />
                                        </TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>{r.deptName || "—"}</TableCell>
                                        <TableCell align="center" sx={{ maxWidth: 280 }}>
                                            <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }} title={r.title}>{r.title}</Typography>
                                            {r.channelName && (
                                                <Box sx={{ mt: 0.2 }} onClick={e => e.stopPropagation()}>
                                                    <ProjectCell channelId={r.channelId} channelName={r.channelName} />
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip label={pay.label} size="small"
                                                sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: pay.bg, color: pay.color }} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip label={src.label} size="small"
                                                sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: src.bg, color: src.color }} />
                                        </TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: 12, color: "#475569" }}>{r.authorName || "—"}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: "#dc2626", whiteSpace: "nowrap" }}>
                                            −{fmtKRWFull(r.amount)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip label={sm.label} size="small"
                                                sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: sm.bg, color: sm.color }} />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                <TableCell sx={{ fontWeight: 800 }} colSpan={8}>합계 ({rows.length}건)</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, color: "#dc2626", whiteSpace: "nowrap" }}>
                                    −{fmtKRWFull(total)}
                                </TableCell>
                                <TableCell />
                            </TableRow>
                        </>}
                    </TableBody>
                </ResizableTable>}
            </Paper>

            {/* 상세 모달 — 수기·고정비 등 결재연동이 아닌 건 (결재연동은 결재 상세로 이동) */}
            <FormModal open={Boolean(detailRow)} onClose={() => setDetailRow(null)} title="지출 예정 상세" maxWidth="sm" cancelLabel="닫기">
                {detailRow && (() => {
                    const sm  = STATUS_META[detailRow.status] || STATUS_META.SCHEDULED;
                    const src = sourceKind(detailRow);
                    const pay = payKind(detailRow);
                    const chip = (o, fs = 11) => (
                        <Chip label={o.label} size="small"
                            sx={{ height: 22, fontSize: fs, fontWeight: 700, bgcolor: o.bg, color: o.color }} />
                    );
                    return (
                        <Stack spacing={2}>
                            <FormField label="구분">
                                <ReadBox>{detailRow.flowType === "INCOME" ? "입금 (수입)" : "출금 (지출 예정)"}</ReadBox>
                            </FormField>
                            <FormField label="내용">
                                <ReadBox>{detailRow.title}</ReadBox>
                            </FormField>
                            <Box sx={{ display: "flex", gap: 1.5 }}>
                                <FormField label="금액" sx={{ flex: 1, minWidth: 0 }}>
                                    <ReadBox>
                                        <Box component="span" sx={{ color: "#dc2626", fontWeight: 800 }}>
                                            −{fmtKRWFull(detailRow.amount)}
                                        </Box>
                                    </ReadBox>
                                </FormField>
                                <FormField label="송금 예정일" sx={{ flex: 1, minWidth: 0 }}>
                                    <ReadBox>{detailRow.scheduledDate}</ReadBox>
                                </FormField>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1.5 }}>
                                <FormField label="상태" sx={{ flex: 1, minWidth: 0 }}><ReadBox>{chip(sm)}</ReadBox></FormField>
                                <FormField label="결제 유형" sx={{ flex: 1, minWidth: 0 }}><ReadBox>{chip(pay)}</ReadBox></FormField>
                                <FormField label="출처" sx={{ flex: 1, minWidth: 0 }}><ReadBox>{chip(src)}</ReadBox></FormField>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1.5 }}>
                                <FormField label="팀" sx={{ flex: 1, minWidth: 0 }}>
                                    <ReadBox>{detailRow.deptName}</ReadBox>
                                </FormField>
                                <FormField label="프로젝트 채널" sx={{ flex: 1, minWidth: 0 }}>
                                    <ReadBox>{detailRow.channelName}</ReadBox>
                                </FormField>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1.5 }}>
                                <FormField label="거래내용 구분" sx={{ flex: 1, minWidth: 0 }}>
                                    <ReadBox>{detailRow.category}</ReadBox>
                                </FormField>
                                <FormField label="결재일" sx={{ flex: 1, minWidth: 0 }}>
                                    <ReadBox>{detailRow.approvalDate}</ReadBox>
                                </FormField>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1.5 }}>
                                <FormField label="거래처" sx={{ flex: 1, minWidth: 0 }}>
                                    <ReadBox>{detailRow.vendorName}</ReadBox>
                                </FormField>
                                <FormField label="작성자/기안자" sx={{ flex: 1, minWidth: 0 }}>
                                    <ReadBox>{detailRow.authorName}</ReadBox>
                                </FormField>
                            </Box>
                            <FormField label="비고">
                                <ReadBox multiline>{detailRow.note}</ReadBox>
                            </FormField>
                        </Stack>
                    );
                })()}
            </FormModal>
        </Box>
    );
}
